//
// # Instances (and "Instance-Like" things) Base Class
//

// Local Imports
import { model, Direction, Rotation } from "GenAlphaModel";
import { MousePos } from "../../../mousepos";
import { Dots, DotParent } from "../dot";
import { Entity } from "../../../tree";
import { RedrawRequest } from "../../../redraw";

// # Instance-Like Base Class
export abstract class Base<Data extends model.schematic.HasXform>
  extends Entity<Data>
  implements DotParent, model.schematic.Xformable
{
  readonly dots = new Dots();

  // Boolean indication of whether `mousePos` is inside the instance.
  abstract hitTest(mousePos: MousePos): boolean;

  // Boolean indication of whether `bbox` intersects the instance's selection region.
  abstract intersectsBbox(bbox: model.Bbox): boolean;

  // The `model.Xformable` interface
  override get xform(): model.Xform {
    return this.data.xform;
  }

  // Write `xform` and rebuild. `Data extends HasXform`, so writing through the
  // `HasXform` view is sound — and avoids `update({ xform } as Partial<Data>)`,
  // which can't be expressed cast-free (a `Data` subtype could narrow `xform`).
  // Mirrors `update`'s clear/build/redraw; the write is in place, matching how
  // `rotate`/`flip` already mutate `xform`.
  private setXform(xform: model.Xform): RedrawRequest {
    this.clear();
    const withXform: model.schematic.HasXform = this.data;
    withXform.xform = xform;
    this.build();
    return this.root.requestRedraw();
  }

  move = (to: model.Xform): RedrawRequest => this.setXform(to);

  // Rotate by one 90 degree increment
  rotate = (): RedrawRequest => {
    const { xform } = this.data;
    xform.rotation = Rotation.next(xform.rotation);
    return this.setXform(xform);
  };

  // Flip in direction `dir`
  flip = (dir: Direction): RedrawRequest => {
    const { xform } = this.data;
    if (
      // If rotated by 90 or 270 degrees, invert `dir`
      // so that we flip in the direction as the user sees it,
      // not relative to the instance coordinates.
      xform.rotation === Rotation.R90 ||
      xform.rotation === Rotation.R270
    ) {
      dir = Direction.other(dir);
    }
    // Always flip vertically. Horizontal flips are comprised of a vertical flip and two rotations.
    xform.reflected = !xform.reflected;
    if (dir === Direction.Horiz) {
      xform.rotation = Rotation.next(Rotation.next(xform.rotation));
    }
    return this.setXform(xform);
  };

  // Abort drawing an in-progress instance.
  abort = () => {};
}
